/*
 * Copyright (c) 2025 by PROS, Inc.  All Rights Reserved.
 * This software is the confidential and proprietary information of
 * PROS, Inc. ("Confidential Information").
 * You may not disclose such Confidential Information, and may only
 * use such Confidential Information in accordance with the terms of
 * the license agreement you entered into with PROS.
 */
import { __decorate } from "tslib";
import { html, css, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { withEnterKey } from '../internal/with-enter-key.js';
import { prosAiChatUi } from '../pros-ai-chat-ui.js';
import * as Assets from '../assets.js';
/**
 * WebComponent.
 */
let PwcAiButton = class PwcAiButton extends LitElement {
    constructor() {
        super(...arguments);
        this.label = '';
        this.flowName = '';
        this.welcomeMessage = '';
        this.newConversationOnContextChange = false;
        this.enableHitl = false;
        this.isHidden = false;
        /**
         * Custom property accessors.
         */
        this._context = {};
        this._suggestedPrompts = [];
    }
    /**
     * Get/Set the context.
     */
    set context(value) {
        this._context = value;
        this._handleContextChange(value);
    }
    get context() {
        return this._context;
    }
    /**
     * Get/Set the suggested prompts.
     */
    set suggestedPrompts(value) {
        this._suggestedPrompts = value;
    }
    get suggestedPrompts() {
        return this._suggestedPrompts;
    }
    /**
     * @inheritdoc
     */
    connectedCallback() {
        // eslint-disable-next-line wc/guard-super-call
        super.connectedCallback();
        // register.
        prosAiChatUi.registerPwcAiButton(this);
    }
    /**
     * @inheritdoc
     */
    disconnectedCallback() {
        // eslint-disable-next-line wc/guard-super-call
        super.disconnectedCallback();
        // unregister.
        prosAiChatUi.unregisterPwcAiButton(this);
    }
    /**
     * @override
     */
    attributeChangedCallback(name, _old, value) {
        // eslint-disable-next-line wc/guard-super-call
        super.attributeChangedCallback(name, _old, value);
        // emit a context changed event.
        if (name === 'context' && value !== null) {
            this._handleContextChange(value);
        }
    }
    /**
     * @override
     */
    render() {
        // trivial case: the button is disabled.
        if (this.isHidden) {
            return html ``;
        }
        return html `
      <div
        class="ai-button"
        @click=${() => this._handleOpenChat()}
        @keydown=${(event) => withEnterKey(event, () => this._handleOpenChat())}
      >
        <div class="content">
          <slot>
            <div class="default">
              ${Assets.iconAi()}
              <div class="label">${this.label}</div>
            </div>
          </slot>
        </div>
      </div>
    `;
    }
    /**
     * Handle the open chat action.
     */
    _handleOpenChat() {
        // create custom event.
        const event = new CustomEvent('pwc-ai-button-open-chat', {
            detail: {
                context: this._context,
                enableHitl: this.enableHitl,
                flowName: this.flowName,
                newConversationOnContextChange: this.newConversationOnContextChange,
                welcomeMessage: this.welcomeMessage,
                suggestedPrompts: this._suggestedPrompts,
            },
        });
        this.dispatchEvent(event);
    }
    /**
     * Handle the context change.
     */
    _handleContextChange(value) {
        // guard.
        if (this.newConversationOnContextChange) {
            return;
        }
        let context;
        if (typeof value === 'object') {
            // object.
            context = value;
        }
        else if (typeof value === 'string') {
            // string.
            context = JSON.parse(value);
        }
        else {
            // unsupported.
            throw new Error('Unsupported context value type.');
        }
        // create custom event.
        const event = new CustomEvent('pwc-ai-button-context-change', {
            detail: {
                flowName: this.flowName,
                context,
            },
        });
        this.dispatchEvent(event);
    }
};
// styles.
PwcAiButton.styles = css `
    :host {
      display: inline-block;
      position: relative;

      /* Colors */
      --pwc-ai-button-background-color: #f9f6fe;

      /* Sizes */
      --pwc-ai-button-border-width: 1px;
      --pwc-ai-button-border-radius: 12px;
      --pwc-ai-button-font-size: var(--pwc-font-size-small, 0.875rem);
    }

    .ai-button {
      display: flex;
      flex-direction: row;
      display: flex;
      flex: auto;
      padding: var(--pwc-ai-button-border-width);
      border-radius: var(--pwc-ai-button-border-radius);
      background: linear-gradient(
        357deg,
        #31cdff,
        #0b3cea,
        #7056df,
        #a54fdf,
        #c16067,
        #ff8921
      );
      position: relative;
      pointer-events: all;
    }

    .ai-button .content {
      display: flex;
      flex-direction: row;
      flex: auto;
      background-color: var(--pwc-ai-button-background-color);
      border: none;
      border-radius: calc(
        var(--pwc-ai-button-border-radius) - var(--pwc-ai-button-border-width)
      );
      outline: 0;
      color: #212121;
      font-weight: 500;
      overflow: hidden;
      cursor: pointer;
    }

    .ai-button .content:hover {
      background: linear-gradient(
        171deg,
        rgb(182, 198, 220) 25%,
        rgb(195, 189, 229) 50%,
        rgb(228, 193, 194) 80%
      );
    }

    .ai-button .content .default {
      display: flex;
      flex-direction: row;
      flex: auto;
      align-items: center;
      padding: 1px 2px 2px 2px;
    }

    .ai-button .icon-ai {
      flex: none;
      height: 9px;
      width: 9px;
      margin: 1px 2px 0px 4px;
    }

    .ai-button .label {
      white-space: nowrap;
      font-size: var(--pwc-ai-button-font-size);
      margin-right: 6px;
    }
  `;
__decorate([
    property({ attribute: true, type: String })
], PwcAiButton.prototype, "label", void 0);
__decorate([
    property({ attribute: 'flow-name', type: String })
], PwcAiButton.prototype, "flowName", void 0);
__decorate([
    property({ attribute: 'welcome-message', type: String })
], PwcAiButton.prototype, "welcomeMessage", void 0);
__decorate([
    property({ attribute: 'new-conversation-on-context-change', type: Boolean })
], PwcAiButton.prototype, "newConversationOnContextChange", void 0);
__decorate([
    property({ attribute: 'enable-hitl', type: Boolean })
], PwcAiButton.prototype, "enableHitl", void 0);
__decorate([
    property({ attribute: 'is-hidden', type: Boolean })
], PwcAiButton.prototype, "isHidden", void 0);
__decorate([
    property({ attribute: 'context', type: Object })
], PwcAiButton.prototype, "context", null);
__decorate([
    property({ attribute: 'suggested-prompts', type: Array })
], PwcAiButton.prototype, "suggestedPrompts", null);
PwcAiButton = __decorate([
    customElement('pwc-ai-button')
], PwcAiButton);
export { PwcAiButton };
//# sourceMappingURL=pwc-ai-button.js.map